import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, CreditCard, ExternalLink, Plus, Receipt } from 'lucide-react';
import {
  type PaymentIntent,
  type PaymentProvider,
  createIntent,
  createProvider,
  createStripeCheckout,
  listIntents,
  listProviders,
  markIntentPaid,
} from '@/api/payments';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

function isAdmin(role: string | undefined): boolean {
  return role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';
}

function fmtAmount(amount: number | null | undefined, currency: string): string {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

const STATUS_STYLES: Record<string, string> = {
  succeeded: 'bg-emerald-900/30 text-emerald-300',
  processing: 'bg-sky-900/30 text-sky-300',
  pending: 'bg-amber-900/30 text-amber-300',
};

export function PaymentsPage() {
  const { t } = useTranslation();
  const admin = isAdmin(useAuthStore((s) => s.user?.role));
  const [tab, setTab] = useState<'intents' | 'providers'>('intents');
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [intents, setIntents] = useState<PaymentIntent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProvider, setShowProvider] = useState(false);
  const [showIntent, setShowIntent] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listProviders().catch(() => []), listIntents().catch(() => [])])
      .then(([p, i]) => {
        setProviders(p);
        setIntents(i);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const providerName = (id: string) => providers.find((p) => p.id === id)?.name ?? id.slice(0, 8);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{t('payments.title')}</h1>
        <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('payments.subtitle')}</p>
      </header>

      <div className="flex items-center justify-between border-b border-[var(--color-lop-border)]">
        <div className="flex gap-1">
          <TabButton active={tab === 'intents'} onClick={() => setTab('intents')} testId="payments-tab-intents">
            <Receipt className="mr-1 inline h-4 w-4" />
            {t('payments.tab.intents')}
          </TabButton>
          <TabButton active={tab === 'providers'} onClick={() => setTab('providers')} testId="payments-tab-providers">
            <CreditCard className="mr-1 inline h-4 w-4" />
            {t('payments.tab.providers')}
          </TabButton>
        </div>
        {tab === 'intents' && (
          <Button
            size="sm"
            className="mb-2"
            onClick={() => setShowIntent(true)}
            disabled={providers.length === 0}
            data-testid="payments-new-intent"
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('payments.newIntent')}
          </Button>
        )}
        {tab === 'providers' && admin && (
          <Button size="sm" className="mb-2" onClick={() => setShowProvider(true)} data-testid="payments-new-provider">
            <Plus className="mr-1 h-4 w-4" />
            {t('payments.newProvider')}
          </Button>
        )}
      </div>

      {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}

      {!loading && tab === 'intents' && (
        <div className="overflow-hidden rounded-lg border bg-card">
          {intents.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('payments.intentsEmpty')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-[var(--color-lop-text-muted)]">
                  <th className="px-4 py-2 font-medium">{t('payments.col.amount')}</th>
                  <th className="px-4 py-2 font-medium">{t('payments.col.provider')}</th>
                  <th className="px-4 py-2 font-medium">{t('payments.col.description')}</th>
                  <th className="px-4 py-2 font-medium">{t('payments.col.status')}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-lop-border)]">
                {intents.map((it) => (
                  <tr key={it.id} data-testid="payments-intent-row">
                    <td className="px-4 py-3 font-medium text-[var(--color-lop-text-primary)]">
                      {fmtAmount(it.amount, it.currency)}
                    </td>
                    <td className="px-4 py-3 text-[var(--color-lop-text-secondary)]">{providerName(it.providerId)}</td>
                    <td className="px-4 py-3 text-[var(--color-lop-text-secondary)]">{it.description ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          STATUS_STYLES[it.status] ?? 'bg-zinc-800 text-zinc-300'
                        }`}
                      >
                        {t(`payments.status.${it.status}`, it.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {admin && it.status !== 'succeeded' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            await markIntentPaid(it.id);
                            load();
                          }}
                          data-testid="payments-mark-paid"
                        >
                          <BadgeCheck className="mr-1 h-4 w-4" />
                          {t('payments.markPaid')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && tab === 'providers' && (
        <div className="overflow-hidden rounded-lg border bg-card">
          {providers.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('payments.providersEmpty')}</p>
          ) : (
            <ul className="divide-y divide-[var(--color-lop-border)]">
              {providers.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid="payments-provider-row">
                  <CreditCard className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[var(--color-lop-text-primary)]">{p.name}</p>
                    <p className="text-xs text-[var(--color-lop-text-muted)]">
                      {p.provider} · {p.code}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      p.enabled ? 'bg-emerald-900/30 text-emerald-300' : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {p.enabled ? t('payments.enabled') : t('payments.disabled')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showProvider && (
        <ProviderModal
          onClose={() => setShowProvider(false)}
          onCreated={() => {
            setShowProvider(false);
            load();
          }}
        />
      )}
      {showIntent && (
        <IntentModal
          providers={providers}
          onClose={() => setShowIntent(false)}
          onCreated={() => {
            setShowIntent(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
        active
          ? 'border-[var(--color-lop-accent)] text-[var(--color-lop-text-primary)]'
          : 'border-transparent text-[var(--color-lop-text-muted)] hover:text-[var(--color-lop-text-secondary)]'
      }`}
    >
      {children}
    </button>
  );
}

function ProviderModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('stripe');
  const [publicKey, setPublicKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!code.trim() || !name.trim() || !provider.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await createProvider({
        code: code.trim(),
        name: name.trim(),
        provider: provider.trim(),
        publicKey: publicKey.trim() || undefined,
        secretKey: secretKey.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create provider');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('payments.providerModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('payments.providerModal.gateway')}
            </label>
            <Select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="payments-provider-gateway">
              <option value="stripe">Stripe</option>
              <option value="mercadopago">MercadoPago</option>
              <option value="paypal">PayPal</option>
              <option value="manual">{t('payments.providerModal.manual')}</option>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('payments.providerModal.code')}
            </label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="payments-provider-code" />
          </div>
        </div>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('payments.providerModal.name')}
        </label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-3" data-testid="payments-provider-name" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('payments.providerModal.publicKey')}
        </label>
        <Input value={publicKey} onChange={(e) => setPublicKey(e.target.value)} className="mb-3" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('payments.providerModal.secretKey')}
        </label>
        <Input
          type="password"
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          className="mb-1"
        />
        <p className="mb-4 text-[10px] text-[var(--color-lop-text-muted)]">{t('payments.providerModal.secretHint')}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="payments-provider-submit">
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function IntentModal({
  providers,
  onClose,
  onCreated,
}: {
  providers: PaymentProvider[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [providerId, setProviderId] = useState(providers[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [description, setDescription] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isStripe = providers.find((p) => p.id === providerId)?.provider === 'stripe';

  function validAmount(): number | null {
    const amt = Number(amount);
    if (!providerId || !currency.trim() || !Number.isFinite(amt) || amt <= 0) {
      setError(t('payments.intentModal.invalid'));
      return null;
    }
    return amt;
  }

  async function submit() {
    const amt = validAmount();
    if (amt == null) return;
    setSubmitting(true);
    setError(null);
    try {
      await createIntent({
        providerId,
        amount: amt,
        currency: currency.trim().toUpperCase(),
        description: description.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create intent');
    } finally {
      setSubmitting(false);
    }
  }

  // Stripe Checkout: create the hosted session and open Stripe's pay page in a
  // new tab. The intent + checkout_url are recorded server-side; the webhook
  // settles it on completion.
  async function payWithStripe() {
    const amt = validAmount();
    if (amt == null) return;
    setSubmitting(true);
    setError(null);
    try {
      const { checkoutUrl } = await createStripeCheckout({
        providerId,
        amount: amt,
        currency: currency.trim().toUpperCase(),
        description: description.trim() || t('payments.title'),
        customerEmail: customerEmail.trim() || undefined,
      });
      window.open(checkoutUrl, '_blank', 'noopener');
      onCreated();
    } catch (err) {
      setError((err as Error)?.message ?? 'Stripe checkout failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('payments.intentModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('payments.intentModal.provider')}
        </label>
        <Select
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
          className="mb-3"
          data-testid="payments-intent-provider"
        >
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('payments.intentModal.amount')}
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payments-intent-amount"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('payments.intentModal.currency')}
            </label>
            <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} />
          </div>
        </div>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('payments.intentModal.description')}
        </label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mb-3" />
        {isStripe && (
          <>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('payments.intentModal.customerEmail')}
            </label>
            <Input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="mb-4"
              data-testid="payments-intent-email"
            />
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          {isStripe && (
            <Button size="sm" onClick={payWithStripe} disabled={submitting} data-testid="payments-stripe-checkout">
              <ExternalLink className="mr-1 h-4 w-4" />
              {t('payments.intentModal.payWithStripe')}
            </Button>
          )}
          <Button size="sm" variant={isStripe ? 'outline' : 'default'} onClick={submit} disabled={submitting} data-testid="payments-intent-submit">
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}
